/**
 * Whether the WhatsApp Web account behind an extension client is linked.
 *
 * The extension states it in its presence record (`waPairing`) so the header
 * indicator can tell "WhatsApp Web is open but logged out / showing a QR" from a
 * working client. Presence alone only proves the tab is online.
 */
export type WaPairing = 'paired' | 'logged_out';

export interface WaPairingInput {
  /** Raw WA Web state: CONNECTED, UNPAIRED, UNPAIRED_IDLE, PAIRING, OPENING, ... */
  state?: string;
  /** What fed the state machine: handleQRChanged, handlePairingCode, handleWaLogout, ... */
  triggerdBy?: string;
  qrcode?: string;
}

/**
 * The pairing state this WA state update implies, or undefined when it says
 * nothing (OPENING / LOADING / health-check noise): the caller keeps the last
 * known value instead of flapping to unknown.
 */
export function deriveWaPairing({ state, triggerdBy, qrcode }: WaPairingInput): WaPairing | undefined {
  if (triggerdBy === 'handleWaLogout') return 'logged_out';
  if (triggerdBy === 'handlePairingCode') return 'logged_out';
  if (triggerdBy === 'handleQRChanged' && qrcode) return 'logged_out';
  if (state === 'UNPAIRED' || state === 'UNPAIRED_IDLE') return 'logged_out';
  // A disconnected stream with a CONNECTED state is a phone/network blip, still linked.
  if (state === 'CONNECTED') return 'paired';
  return undefined;
}

/** Narrow an untrusted presence field to a WaPairing. */
export function parseWaPairing(value: unknown): WaPairing | undefined {
  return value === 'paired' || value === 'logged_out' ? value : undefined;
}
